import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import {
  Button,
  Card,
  CardBody,
  Table,
  Badge,
  Input,
  InputGroup, 
  InputGroupText,
  Spinner,
  Alert,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
} from "reactstrap";
import StatusModal from "./StatusModal";
import OrdersModal from "./OrdersModal";
import { updateOrderStatusRequest } from "../../store/orderManagement/actions";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { ar } from "date-fns/locale";
import { FiSearch, FiEye, FiEdit, FiClock, FiCheck, FiX, FiAlertTriangle } from "react-icons/fi";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "helpers/firebase/firebaseConfig";
import Cookies from "js-cookie";
import { getBookingExecuteDate, isOrderDelayed } from "helpers/api/cancellationHelper";

const OrderManagementDashboard = ({ orders, loading, error }) => {
  const dispatch = useDispatch();
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterValue, setFilterValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [cancellationCount, setCancellationCount] = useState(0);
  const [isServiceDeleted, setIsServiceDeleted] = useState(false);
  const [delayIncidents, setDelayIncidents] = useState([]);
  const [pendingDelayFines, setPendingDelayFines] = useState([]);
  const [delayDataLoading, setDelayDataLoading] = useState(false);
  const [delayDataError, setDelayDataError] = useState(null);
  const [activeMainView, setActiveMainView] = useState("orders");
  const [activeDelayView, setActiveDelayView] = useState("stats");
  const [providerDelayStats, setProviderDelayStats] = useState({
    delay_incident_count: 0,
    delay_warning_count: 0,
    total_delay_fines: 0,
    pending_delay_fines: 0,
  });

  const paymentStatusLabels = {
    paid: "مدفوع",
    pending: "معلق",
    failed: "فشل",
  };

  const deductionStatusLabels = {
    warning_only: "إنذار فقط",
    pending_settlement: "بانتظار التسوية",
    settled: "تم الخصم",
    partial: "خصم جزئي",
  };

  const pendingFineStatusLabels = {
    pending: "معلق",
    applied: "تم الخصم",
    partial: "خصم جزئي",
  };

  const parseTimestamp = (timestamp) => {
    if (!timestamp) return new Date(0);
    if (timestamp?.seconds) return new Date(timestamp.seconds * 1000);
    if (typeof timestamp === "string") {
      const parsed = parseISO(timestamp);
      return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
    }
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? new Date(0) : parsed;
  };

  const formatOrderDate = (timestamp) => {
    const date = parseTimestamp(timestamp);
    return format(date, "yyyy/MM/dd - hh:mm a", { locale: ar });
  };

  const formatExecuteDateWithRelative = (value) => {
    const executeDate = getBookingExecuteDate(value);
    if (!executeDate) {
      return { absolute: "غير متوفر", relative: "" };
    }

    return {
      absolute: format(executeDate, "yyyy/MM/dd - hh:mm a", { locale: ar }),
      relative: formatDistanceToNow(executeDate, { addSuffix: true, locale: ar }),
    };
  };

  const getPaymentBadge = (paymentStatus) => {
    const normalized = String(paymentStatus || "").toLowerCase();
    const colorMap = {
      paid: "success",
      pending: "warning",
      failed: "danger",
    };
    return (
      <Badge color={colorMap[normalized] || "secondary"}>
        {paymentStatusLabels[normalized] || paymentStatus || "غير متوفر"}
      </Badge>
    );
  };

  const delayedOrders = useMemo(() => filteredOrders.filter(order => isOrderDelayed(order)), [filteredOrders]);

  // Filter and sort orders
  useEffect(() => {
    let result = [...orders];
    
    // Apply status filter
    if (filterValue) {
      result = result.filter(order => order.booking_status === filterValue);
    }
    
    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(order =>
        (order.app_user_id?.username?.toLowerCase().includes(query)) ||
        (order.serviceName?.toLowerCase().includes(query)) ||
        (order.app_user_id?.mobile?.includes(searchQuery)) ||
        (order.id?.includes(searchQuery))
      );
    }
    
    // Sort by newest first (prefer updatedAt, fallback to create_at)
    const getSortDate = order => {
      const primary = parseTimestamp(order.updatedAt);
      const fallback = parseTimestamp(order.create_at);
      return primary?.getTime() ? primary : fallback;
    };
    result.sort((a, b) => getSortDate(b) - getSortDate(a));

    setFilteredOrders(result);
  }, [orders, filterValue, searchQuery]);

  // Fetch provider delay stats, incidents and pending delay fines
  useEffect(() => {
    const fetchProviderDelayData = async () => {
      try {
        setDelayDataLoading(true);
        setDelayDataError(null);

        const currentProviderId = Cookies.get("usersessionId", {
          secure: true,
          sameSite: "strict",
          path: "/",
        });

        if (!currentProviderId) {
          return;
        }

        const providerRef = doc(db, "service_providers", currentProviderId);

        const delayIncidentsQuery = query(
          collection(db, "cancellation_track"),
          where("service_provider_id", "==", currentProviderId),
          where("cancellation_type", "==", "provider_delay")
        );

        const pendingDelayFinesQuery = query(
          collection(db, "pending_fine_deductions"),
          where("service_provider_id", "==", currentProviderId),
          where("source_type", "==", "provider_delay")
        );

        const [providerSnap, incidentsSnap, pendingFinesSnap] = await Promise.all([
          getDoc(providerRef),
          getDocs(delayIncidentsQuery),
          getDocs(pendingDelayFinesQuery),
        ]);

        if (providerSnap.exists()) {
          const providerData = providerSnap.data() || {};
          setCancellationCount(providerData.order_cancellation_count || 0);
          setProviderDelayStats({
            delay_incident_count: providerData.delay_incident_count || 0,
            delay_warning_count: providerData.delay_warning_count || 0,
            total_delay_fines: providerData.total_delay_fines || 0,
            pending_delay_fines: providerData.pending_delay_fines || 0,
          });
        }

        const incidents = incidentsSnap.docs
          .map(item => ({ id: item.id, ...item.data() }))
          .sort((a, b) => {
            const left = parseTimestamp(a.created_at || a.cancellation_date);
            const right = parseTimestamp(b.created_at || b.cancellation_date);
            return right - left;
          });

        const pendingFines = pendingFinesSnap.docs
          .map(item => ({ id: item.id, ...item.data() }))
          .filter(item => item.status === "pending")
          .sort((a, b) => parseTimestamp(b.created_at) - parseTimestamp(a.created_at));

        setDelayIncidents(incidents);
        setPendingDelayFines(pendingFines);
      } catch (err) {
        console.error("Error fetching provider delay data:", err);
        setDelayDataError("تعذر تحميل بيانات التأخير حالياً");
      } finally {
        setDelayDataLoading(false);
      }
    };

    fetchProviderDelayData();
  }, []);

  const toggleStatusModal = (orderId = null, status = "", order = null) => {
    setSelectedOrderId(orderId);
    setSelectedStatus(status);
    setSelectedOrder(order);
    // Check if the service referenced in the order is soft-deleted
    const serviceIsDeleted = order?.serviceProviderServices?.is_deleted === true;
    setIsServiceDeleted(serviceIsDeleted);
    setStatusModalOpen(!statusModalOpen);
  };

  const toggleDetailsModal = (order = null) => {
    setSelectedOrder(order);
    setDetailsModalOpen(!detailsModalOpen);
  };

  const handleStatusUpdate = (orderId, newStatus) => {
    dispatch(updateOrderStatusRequest(orderId, newStatus));
    setFilteredOrders(prev => 
      prev.map(order => 
        order.id === orderId ? { ...order, booking_status: newStatus } : order
      )
    );
    toggleStatusModal();
  };

  const StatusBadge = ({ status }) => {
    const statusMap = {
      pending: { color: "warning", icon: <FiClock />, text: "قيد الانتظار" },
      approved: { color: "success", icon: <FiCheck />, text: "مقبول" },
      cancelled: { color: "danger", icon: <FiX />, text: "تم إلغاء الطلب" },
      completed: { color: "primary", icon: <FiCheck />, text: "مكتمل" },
      progress: { color: "primary", icon: <FiCheck />, text: "جارٍ التنفيذ" },
      cancelled_by_user: { color: "secondary", icon: <FiX />, text: "ملغى من العميل" },
      cancelled_by_provider: { color: "danger", icon: <FiX />, text: "ملغى من مقدم الخدمة" },
      cancelled_by_admin: { color: "dark", icon: <FiX />, text: "ملغى من الإدارة" },
    };

    const currentStatus = statusMap[status] || { color: "secondary", icon: null, text: status };

    return (
      <Badge color={currentStatus.color} className="d-flex align-items-center py-2">
        {currentStatus.icon}
        <span className="ms-2">{currentStatus.text}</span>
      </Badge>
    );
  };

  const pendingDelayTotal = pendingDelayFines.reduce((sum, row) => sum + Number(row.pending_amount || 0), 0);

  return (
    <div className="page-content" dir="rtl">
      <h2 className="mb-4">لوحة إدارة الطلبات</h2>

      <Card className="mb-4">
        <CardBody>
          <Nav tabs className="mb-0">
            <NavItem>
              <NavLink
                href="#"
                active={activeMainView === "orders"}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveMainView("orders");
                }}
              >
                لوحة إدارة الطلبات
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                href="#"
                active={activeMainView === "delay"}
                onClick={(e) => {
                  e.preventDefault();
                  setActiveMainView("delay");
                }}
              >
                متابعة التأخير
              </NavLink>
            </NavItem>
          </Nav>
        </CardBody>
      </Card>

      {activeMainView === "orders" && (
        <>
          {/* Cancellation Count Warning Alert */}
          {cancellationCount > 0 && (
            <Alert
              color={cancellationCount >= 5 ? "danger" : cancellationCount >= 3 ? "warning" : "info"}
              className="mb-4 d-flex align-items-center"
            >
              <FiAlertTriangle className="me-3" size={24} />
              <div>
                <strong>عدد الإلغاءات: {cancellationCount}</strong>
                <p className="mb-0 small mt-2">
                  {cancellationCount >= 5
                    ? "تنبيه: عدد الإلغاءات مرتفع جداً. قد يؤدي هذا إلى تعليق حسابك."
                    : cancellationCount >= 3
                    ? "تحذير: عدد الإلغاءات يتزايد. يرجى تقليل الإلغاءات لتجنب تعليق الحساب."
                    : "لديك عمليات إلغاء للطلبات. حاول تقليل هذا العدد."}
                </p>
              </div>
            </Alert>
          )}

          {/* Search and Filter Section */}
          <Card className="mb-4">
            <CardBody>
              <div className="row g-3">
                <div className="col-md-8">
                  <InputGroup>
                    <InputGroupText>
                      <FiSearch />
                    </InputGroupText>
                    <Input
                      type="text"
                      placeholder="ابحث عن الطلبات (بالاسم، رقم الهاتف، اسم الخدمة، رقم الطلب...)"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </InputGroup>
                </div>
                <div className="col-md-4">
                  <Input
                    type="select"
                    value={filterValue}
                    onChange={(e) => setFilterValue(e.target.value)}
                    className="form-select"
                  >
                    <option value="">جميع الحالات</option>
                    <option value="pending">قيد الانتظار</option>
                    <option value="approved">مقبول</option>
                    <option value="completed">مكتمل</option>
                    <option value="rejected">مرفوض</option>
                    <option value="progress">قيد التنفيذ</option>
                  </Input>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Orders Table */}
          <Card className="mb-4">
            <CardBody>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2">جاري تحميل الطلبات...</p>
                </div>
              ) : error ? (
                <Alert color="danger">
                  <h5 className="alert-heading">حدث خطأ!</h5>
                  <p>{error}</p>
                </Alert>
              ) : filteredOrders.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-muted">لا توجد طلبات متطابقة مع بحثك</p>
                  <Button
                    color="secondary"
                    onClick={() => {
                      setFilterValue("");
                      setSearchQuery("");
                    }}
                  >
                    مسح الفلاتر
                  </Button>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle">
                    <thead>
                      <tr>
                        <th>تاريخ الطلب</th>
                        <th>اسم العميل</th>
                        <th>الخدمة</th>
                        <th>تاريخ التنفيذ</th>
                        <th>رقم الهاتف</th>
                        <th> الدفع</th>
                        <th>الحالة</th>
                        <th>التأخير</th>
                        <th>الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => {
                        const delayed = isOrderDelayed(order);
                        const executeDateInfo = formatExecuteDateWithRelative(order.booking_execute_date);

                        return (
                        <tr key={order.id} className={delayed ? "table-danger" : ""}>
                          <td>{formatOrderDate(order.create_at)}</td>
                          <td>{order.app_user_id?.username || "غير متوفر"}</td>
                          <td>{order.serviceProviderServices?.name || "غير متوفر"}</td>
                          <td>
                            <div>{executeDateInfo.absolute}</div>
                            <small className="text-muted">{executeDateInfo.relative}</small>
                          </td>
                          <td style={{ direction: "ltr" }}>
                            {order.app_user_id?.mobile || "غير متوفر"}
                          </td>
                          <td>{getPaymentBadge(order.paymentStatus)}</td>
                          <td>
                            <StatusBadge status={order.booking_status} />
                          </td>
                          <td>
                            {delayed ? (
                              <Badge color="danger">متأخر</Badge>
                            ) : (
                              <Badge color="light" className="text-dark">في الموعد</Badge>
                            )}
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                color="primary"
                                size="sm"
                                onClick={() => toggleDetailsModal(order)}
                                className="d-flex align-items-center"
                              >
                                <FiEye className="me-1" />
                                التفاصيل
                              </Button>
                              <Button
                                color="success"
                                size="sm"
                                onClick={() => toggleStatusModal(order.id, order.booking_status, order)}
                                className="d-flex align-items-center"
                              >
                                <FiEdit className="me-1" />
                                تغيير
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )})}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {activeMainView === "delay" && (
        <>
          <Card className="mb-4">
            <CardBody>
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                <h5 className="mb-0">مركز متابعة التأخير</h5>
                <Badge color="danger" className="px-3 py-2">{pendingDelayTotal} ر.س غرامات معلقة</Badge>
              </div>

              <Nav pills className="gap-2">
                <NavItem>
                  <NavLink
                    href="#"
                    active={activeDelayView === "stats"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDelayView("stats");
                    }}
                  >
                    إحصائيات التأخير
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    active={activeDelayView === "delayedOrders"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDelayView("delayedOrders");
                    }}
                  >
                    الطلبات المتأخرة
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    active={activeDelayView === "incidents"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDelayView("incidents");
                    }}
                  >
                    سجل حوادث التأخير
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    href="#"
                    active={activeDelayView === "pendingFines"}
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveDelayView("pendingFines");
                    }}
                  >
                    الغرامات المعلقة
                  </NavLink>
                </NavItem>
              </Nav>
            </CardBody>
          </Card>

          {delayDataError && <Alert color="warning">{delayDataError}</Alert>}

          {activeDelayView === "stats" && (
            <Card className="mb-4">
              <CardBody>
                <h5 className="mb-3">إحصائيات التأخير</h5>
                <Row className="g-3">
                  <Col md={6} lg={3}>
                    <div className="border rounded p-3 bg-light">
                      <div className="text-muted">إجمالي حوادث التأخير</div>
                      <h4 className="mb-0">{providerDelayStats.delay_incident_count}</h4>
                    </div>
                  </Col>
                  <Col md={6} lg={3}>
                    <div className="border rounded p-3 bg-light">
                      <div className="text-muted">الإنذارات</div>
                      <h4 className="mb-0">{providerDelayStats.delay_warning_count}</h4>
                    </div>
                  </Col>
                  <Col md={6} lg={3}>
                    <div className="border rounded p-3 bg-light">
                      <div className="text-muted">إجمالي الغرامات</div>
                      <h4 className="mb-0">{providerDelayStats.total_delay_fines} ر.س</h4>
                    </div>
                  </Col>
                  <Col md={6} lg={3}>
                    <div className="border rounded p-3 bg-light">
                      <div className="text-muted">غرامات معلقة</div>
                      <h4 className="mb-0">{providerDelayStats.pending_delay_fines} ر.س</h4>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          )}

          {activeDelayView === "delayedOrders" && (
            <Card className="mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">الطلبات المتأخرة</h5>
                  <Badge color={delayedOrders.length > 0 ? "danger" : "light"} className="px-3 py-2">
                    {delayedOrders.length} طلب
                  </Badge>
                </div>

                {delayDataLoading ? (
                  <div className="text-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : delayedOrders.length === 0 ? (
                  <div className="text-muted">لا توجد طلبات متأخرة حالياً</div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead>
                        <tr>
                          <th>تاريخ الطلب</th>
                          <th>اسم العميل</th>
                          <th>الخدمة</th>
                          <th>تاريخ التنفيذ</th>
                          <th>الفارق الزمني</th>
                          <th>الدفع</th>
                          <th>الحالة</th>
                          <th>التأخير</th>
                        </tr>
                      </thead>
                      <tbody>
                        {delayedOrders.map(order => {
                          const executeDateInfo = formatExecuteDateWithRelative(order.booking_execute_date);
                          return (
                            <tr key={`delayed-${order.id}`} className="table-danger">
                              <td>{formatOrderDate(order.create_at)}</td>
                              <td>{order.app_user_id?.username || "غير متوفر"}</td>
                              <td>{order.serviceProviderServices?.name || "غير متوفر"}</td>
                              <td>{executeDateInfo.absolute}</td>
                              <td>{executeDateInfo.relative}</td>
                              <td>{getPaymentBadge(order.paymentStatus)}</td>
                              <td>
                                <StatusBadge status={order.booking_status} />
                              </td>
                              <td>
                                <Badge color="danger">متأخر</Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {activeDelayView === "incidents" && (
            <Card className="mb-4">
              <CardBody>
                <h5 className="mb-3">سجل حوادث التأخير</h5>
                {delayDataLoading ? (
                  <div className="text-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : delayIncidents.length === 0 ? (
                  <div className="text-muted">لا توجد حوادث تأخير مسجلة</div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>رقم الطلب</th>
                          <th>الخدمة</th>
                          <th>تاريخ الحادثة</th>
                          <th>النتيجة</th>
                          <th>حالة الخصم</th>
                        </tr>
                      </thead>
                      <tbody>
                        {delayIncidents.map(row => (
                          <tr key={row.id}>
                            <td>{row.incident_sequence || "-"}</td>
                            <td>{row.order_id || "-"}</td>
                            <td>{row.service_name || "-"}</td>
                            <td>{formatOrderDate(row.created_at || row.cancellation_date)}</td>
                            <td>
                              {row.is_warning_only ? (
                                <Badge color="warning">إنذار</Badge>
                              ) : (
                                <Badge color="danger">غرامة 30 ر.س</Badge>
                              )}
                            </td>
                            <td>
                              <Badge color="info">
                                {deductionStatusLabels[row.deduction_status] || row.deduction_status || "-"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          )}

          {activeDelayView === "pendingFines" && (
            <Card className="mb-4">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">الغرامات المعلقة</h5>
                  <Badge color="danger" className="px-3 py-2">{pendingDelayTotal} ر.س</Badge>
                </div>

                {delayDataLoading ? (
                  <div className="text-center py-3">
                    <Spinner color="primary" size="sm" />
                  </div>
                ) : pendingDelayFines.length === 0 ? (
                  <div className="text-muted">لا توجد غرامات تأخير معلقة</div>
                ) : (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead>
                        <tr>
                          <th>رقم الطلب</th>
                          <th>السبب</th>
                          <th>المبلغ</th>
                          <th>تاريخ الإنشاء</th>
                          <th>الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingDelayFines.map(row => (
                          <tr key={row.id}>
                            <td>{row.order_id || "-"}</td>
                            <td>{row.reason || "-"}</td>
                            <td>{Number(row.pending_amount || 0)} ر.س</td>
                            <td>{formatOrderDate(row.created_at)}</td>
                            <td>
                              <Badge color="warning">
                                {pendingFineStatusLabels[row.status] || row.status || "-"}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          )}
        </>
      )}

      {/* Modals */}
      <StatusModal
        isOpen={statusModalOpen}
        toggle={toggleStatusModal}
        orderId={selectedOrderId}
        status={selectedStatus}
        order={selectedOrder}
        isServiceDeleted={isServiceDeleted}
        updateOrderStatus={handleStatusUpdate}
      />
      
      <OrdersModal
        isOpen={detailsModalOpen}
        toggle={toggleDetailsModal}
        orderData={selectedOrder}
      />
    </div>
  );
};

OrderManagementDashboard.propTypes = {
  orders: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
};

OrderManagementDashboard.defaultProps = {
  orders: [],
  loading: false,
  error: null,
};

export default OrderManagementDashboard;